/**
 * Classification Engine
 *
 * Mathematical core for choropleth rendering across all ADM levels (0–5)
 * and all metric types in EarthVoice.
 *
 * Phase 2 upgrade: the engine now uses the data profiler for dynamic routing
 * instead of relying solely on hardcoded transform types in METRIC_PROFILES.
 * Three transform paths remain, but the profiler chooses which one based on
 * the actual statistical shape of the data (skewness, kurtosis).
 *
 * Transform paths:
 *   logQuantile — severe power-law (γ₁ > 2.0 or β₂ > 7.0)
 *   linearQuantile — bounded/normal data (default)
 *   diverging — data crosses zero (Symlog transform)
 *   discrete — sparse data (n < k) or degenerate (all identical)
 *
 * Sources:
 *   Grekousis Section 2.2.6 (pp. 75–76): Quantiles
 *   Gemini report pp. 3–6: Break calculators, Symlog
 *   Gaigg Chapter 4: Legend formatting
 */

import { METRIC_ANCHORS } from '../data/metric-anchors.js'
import { profileData } from '../data/data-profiler.js'

// ── Metric Profiles ──────────────────────────────────────────────────────
// Every metric EarthVoice can display has a profile specifying how it
// should be classified, colored, and labeled.

/**
 * Format population-type values with magnitude suffixes.
 * Phase 3 fix: exactly 1 decimal place for all suffixed values (spec §5).
 * Handles negative values for diverging legends.
 */
function fmtPop(v) {
  if (v === 0) return '0'
  const sign = v < 0 ? '-' : ''
  const abs = Math.abs(v)
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(1)}B`
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(1)}M`
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(1)}K`
  return String(Math.round(v))
}

/**
 * Format money values with currency symbol and magnitude suffixes.
 * Phase 3 fix: exactly 1 decimal place for all suffixed values (spec §5).
 * Handles negative values for diverging legends.
 */
function fmtMoney(v) {
  if (v === 0) return '$0'
  const sign = v < 0 ? '-' : ''
  const abs = Math.abs(v)
  if (abs >= 1e12) return `${sign}$${(abs / 1e12).toFixed(1)}T`
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(1)}B`
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`
  return `${sign}$${Math.round(abs).toLocaleString()}`
}

export const METRIC_PROFILES = {
  // ── Population metrics ──
  population: {
    label: 'Population',
    unit: 'people',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 3,
    formatValue: fmtPop,
  },
  pop_est: {
    label: 'Population',
    unit: 'people',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 3,
    formatValue: fmtPop,
  },
  density_per_km2: {
    label: 'Population Density',
    unit: 'per km²',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}/km²`,
  },
  pop_density_km2: {
    label: 'Population Density',
    unit: 'per km²',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}/km²`,
  },
  pop_density: {
    label: 'Population Density',
    unit: 'per km²',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}/km²`,
  },

  // ── Economic metrics ──
  gdp_md: {
    label: 'GDP',
    unit: 'millions USD',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => {
      const usd = v * 1e6 // gdp_md is in millions
      return fmtMoney(usd)
    },
  },
  gdp_total: {
    label: 'GDP (Total)',
    unit: 'USD',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: fmtMoney,
  },
  gdp_per_capita: {
    label: 'GDP per Capita',
    unit: 'USD/person',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `$${Math.round(v).toLocaleString()}`,
  },
  income_grp: {
    label: 'Income Group',
    unit: '',
    transform: 'linear',
    breakCount: 5,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => String(v),
  },

  // ── Human development ──
  hdi: {
    label: 'Human Development Index',
    unit: 'index (0–1)',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'absolute',
    nullThreshold: 0,
    formatValue: (v) => v.toFixed(3),
  },
  life_expectancy: {
    label: 'Life Expectancy',
    unit: 'years',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'absolute',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)} yrs`,
  },

  // ── Spatial-analysis scores (similarity/suitability ops) ──
  // Bounded 0–1, but heavily compressed toward the top (Gower similarity
  // clusters at 0.7–0.97) — scaleType 'relative' so quantile breaks stretch
  // the visible contrast across the actual data range (skill: bounded data
  // → _linearQuantileBreaks; relative scale for compressed distributions).
  similarity: {
    label: 'Similarity',
    unit: 'score (0–1)',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => v.toFixed(2),
  },
  suitability: {
    label: 'Suitability',
    unit: 'score (0–1)',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => v.toFixed(2),
  },

  shared_border_km: {
    label: 'Shared Border',
    unit: 'km',
    transform: 'linear',
    breakCount: 5,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${Math.round(v).toLocaleString()} km`,
  },

  // k-means development typology (ordinal code 1=least … k=most developed).
  // Sequential RdYlGn reads as a development gradient (red → green).
  typology: {
    label: 'Development Type',
    unit: '',
    transform: 'linear',
    breakCount: 5,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: null,
    formatValue: (v) => `Type ${Math.round(v)}`,
  },

  // Temporal % change (diverging around 0; blue = growth, red = decline).
  // Confirmed visually 2026-07-31 against the live map: fast-growing Africa
  // renders blue, contracting Europe renders red. This profile drives BOTH
  // the fill and the legend, so the ramp must be set here — an op's
  // intent.visualization.colorRamp alone does not reach the rendered map.
  pct_change: {
    label: 'Change',
    unit: '%',
    transform: 'diverging',
    breakCount: 7,
    colorScheme: 'RdBu',
    scaleType: 'relative',
    nullThreshold: null,
    formatValue: (v) => `${v > 0 ? '+' : ''}${v.toFixed(1)}%`,
  },

  // LISA / Local Moran's quadrant code (HH=2, HL=1, LH=-1, LL=-2). Diverging
  // RdBu_r reproduces GeoDa's cluster-map colours (HH deep red … LL deep blue).
  lisa_code: {
    label: 'Local Clusters (LISA)',
    unit: '',
    transform: 'diverging',
    breakCount: 4,
    colorScheme: 'RdBu_r',
    scaleType: 'relative',
    nullThreshold: null,
    formatValue: (v) =>
      ({ 2: 'High-High', 1: 'High-Low', '-1': 'Low-High', '-2': 'Low-Low' })[v] || String(v),
  },

  // GWR local standardized slope (diverging around 0; red = strong positive
  // local relationship, blue = negative/absent).
  gwr_slope: {
    label: 'Local Relationship (GWR)',
    unit: 'std. slope',
    transform: 'diverging',
    breakCount: 7,
    colorScheme: 'RdBu_r',
    scaleType: 'relative',
    nullThreshold: null,
    formatValue: (v) => `${v > 0 ? '+' : ''}${v.toFixed(2)}`,
  },

  // Getis-Ord Gi* hot-spot z-score (diverging around 0). RdBu here, not
  // RdBu_r: for indicators where a high value is the good outcome — life
  // expectancy above all — painting the high cluster red reads as an alarm.
  // High clusters render blue, low clusters red, matching the op narration.
  gi_z: {
    label: 'Hot Spots (Gi* z-score)',
    unit: 'z-score',
    transform: 'diverging',
    breakCount: 7,
    colorScheme: 'RdBu',
    scaleType: 'relative',
    nullThreshold: null,
    formatValue: (v) => `${v > 0 ? '+' : ''}${v.toFixed(2)}`,
  },

  // ── Area ──
  area_km2: {
    label: 'Area',
    unit: 'km²',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => {
      if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M km²`
      if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K km²`
      return `${Math.round(v)} km²`
    },
  },

  // ── Environment ──
  co2_emissions: {
    label: 'CO₂ Emissions',
    unit: 'tonnes',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'RdPu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => {
      if (v >= 1e9) return `${(v / 1e9).toFixed(2)}Gt`
      if (v >= 1e6) return `${(v / 1e6).toFixed(1)}Mt`
      return `${Math.round(v).toLocaleString()}t`
    },
  },

  // ── Change metrics (diverging) ──
  population_growth: {
    label: 'Population Growth Rate',
    unit: '% per year',
    transform: 'diverging',
    breakCount: 7,
    colorScheme: 'RdBu',
    scaleType: 'relative',
    nullThreshold: null,
    formatValue: (v) => `${v > 0 ? '+' : ''}${v.toFixed(2)}%`,
  },

  // ── Admin boundary share ──
  pop_pct_national: {
    label: 'Share of National Population',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },

  // ── World Bank Indicators ──────────────────────────────────────────────
  // These profiles match the 20 indicators in country_indicators_latest.parquet.
  // Without these, the classification engine falls back to 'population' profile
  // which shows wrong label/colors for all World Bank indicator choropleths.

  pop_growth_pct: {
    label: 'Population Growth',
    unit: '%',
    transform: 'diverging',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: null,
    formatValue: (v) => `${v > 0 ? '+' : ''}${v.toFixed(2)}%`,
  },
  urban_pct: {
    label: 'Urban Population',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  gdp_usd: {
    label: 'GDP (USD)',
    unit: 'USD',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: fmtMoney,
  },
  gini_index: {
    label: 'Gini Index',
    unit: 'index (0–100)',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => v.toFixed(1),
  },
  unemployment_pct: {
    label: 'Unemployment',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  literacy_rate: {
    label: 'Literacy Rate',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  primary_enrollment: {
    label: 'Primary School Enrollment',
    unit: '% gross',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  secondary_enrollment: {
    label: 'Secondary School Enrollment',
    unit: '% gross',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  infant_mortality: {
    label: 'Infant Mortality',
    unit: 'per 1,000 live births',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}`,
  },
  physicians_per_1k: {
    label: 'Physicians',
    unit: 'per 1,000 people',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(2)}`,
  },
  health_spending_pct: {
    label: 'Health Spending',
    unit: '% of GDP',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  forest_area_pct: {
    label: 'Forest Area',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  co2_per_capita: {
    label: 'CO₂ Emissions per Capita',
    unit: 'metric tons',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'RdPu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}t`,
  },
  renewable_energy_pct: {
    label: 'Renewable Energy',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  electricity_access_pct: {
    label: 'Electricity Access',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  internet_users_pct: {
    label: 'Internet Users',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  mobile_subscriptions: {
    label: 'Mobile Subscriptions',
    unit: 'per 100 people',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}`,
  },

  // ── Demographics (extended) ─────────────────────────────────────────
  birth_rate: {
    label: 'Birth Rate',
    unit: 'per 1,000 people',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}`,
  },
  death_rate: {
    label: 'Death Rate',
    unit: 'per 1,000 people',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}`,
  },
  fertility_rate: {
    label: 'Fertility Rate',
    unit: 'births per woman',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(2)}`,
  },
  age_dependency_ratio: {
    label: 'Age Dependency Ratio',
    unit: '% of working-age',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  pop_ages_0_14_pct: {
    label: 'Population Ages 0–14',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  pop_ages_65_plus_pct: {
    label: 'Population Ages 65+',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  net_migration: {
    label: 'Net Migration',
    unit: 'people',
    transform: 'diverging',
    breakCount: 7,
    colorScheme: 'RdBu',
    scaleType: 'relative',
    nullThreshold: null,
    formatValue: fmtPop,
  },
  adolescent_fertility: {
    label: 'Adolescent Fertility Rate',
    unit: 'births per 1,000 women ages 15–19',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}`,
  },
  pop_density_wb: {
    label: 'Population Density',
    unit: 'per km²',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}/km²`,
  },

  // ── Economics (extended) ────────────────────────────────────────────
  gdp_growth_pct: {
    label: 'GDP Growth',
    unit: '%',
    transform: 'diverging',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: null,
    formatValue: (v) => `${v > 0 ? '+' : ''}${v.toFixed(1)}%`,
  },
  gni_per_capita: {
    label: 'GNI per Capita',
    unit: 'USD',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `$${Math.round(v).toLocaleString()}`,
  },
  poverty_headcount: {
    label: 'Poverty Headcount',
    unit: '% at $2.15/day',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  unemployment_female_pct: {
    label: 'Female Unemployment',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  inflation_pct: {
    label: 'Inflation Rate',
    unit: '% annual',
    transform: 'diverging',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: null,
    formatValue: (v) => `${v > 0 ? '+' : ''}${v.toFixed(1)}%`,
  },
  // NOTE: trade_pct_gdp / exports_pct_gdp / imports_pct_gdp are defined once,
  // further down (WTO trade block) — those later definitions were already
  // winning at runtime; earlier duplicates removed (no-dupe-keys).
  fdi_pct_gdp: {
    label: 'Foreign Direct Investment',
    unit: '% of GDP',
    transform: 'diverging',
    breakCount: 7,
    colorScheme: 'RdBu',
    scaleType: 'relative',
    nullThreshold: null,
    formatValue: (v) => `${v > 0 ? '+' : ''}${v.toFixed(1)}%`,
  },
  govt_debt_pct_gdp: {
    label: 'Government Debt',
    unit: '% of GDP',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  current_account_pct: {
    label: 'Current Account Balance',
    unit: '% of GDP',
    transform: 'diverging',
    breakCount: 7,
    colorScheme: 'RdBu',
    scaleType: 'relative',
    nullThreshold: null,
    formatValue: (v) => `${v > 0 ? '+' : ''}${v.toFixed(1)}%`,
  },
  labor_force_participation: {
    label: 'Labor Force Participation',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },

  // ── Economic structure ──────────────────────────────────────────────
  agriculture_pct_gdp: {
    label: 'Agriculture',
    unit: '% of GDP',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  industry_pct_gdp: {
    label: 'Industry',
    unit: '% of GDP',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  services_pct_gdp: {
    label: 'Services',
    unit: '% of GDP',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  employment_agriculture_pct: {
    label: 'Employment in Agriculture',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  employment_industry_pct: {
    label: 'Employment in Industry',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  employment_services_pct: {
    label: 'Employment in Services',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },

  // ── Education (extended) ────────────────────────────────────────────
  literacy_rate_female: {
    label: 'Female Literacy Rate',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  tertiary_enrollment: {
    label: 'Tertiary School Enrollment',
    unit: '% gross',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  primary_completion: {
    label: 'Primary Completion Rate',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  education_spending_pct: {
    label: 'Education Spending',
    unit: '% of GDP',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  compulsory_education_yrs: {
    label: 'Compulsory Education',
    unit: 'years',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(0)} yrs`,
  },

  // ── Health (extended) ───────────────────────────────────────────────
  life_expectancy_female: {
    label: 'Female Life Expectancy',
    unit: 'years',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'absolute',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)} yrs`,
  },
  life_expectancy_male: {
    label: 'Male Life Expectancy',
    unit: 'years',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'absolute',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)} yrs`,
  },
  // NOTE: under5_mortality / maternal_mortality are defined once, further
  // down (WHO health block) — those later definitions were already winning
  // at runtime; earlier duplicates removed (no-dupe-keys).
  hospital_beds_per_1k: {
    label: 'Hospital Beds',
    unit: 'per 1,000 people',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}`,
  },
  clean_water_pct: {
    label: 'Clean Water Access',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  basic_sanitation_pct: {
    label: 'Basic Sanitation',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  measles_immunization_pct: {
    label: 'Measles Immunization',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  tuberculosis_incidence: {
    label: 'Tuberculosis Incidence',
    unit: 'per 100,000 people',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${Math.round(v).toLocaleString()}`,
  },
  obesity_prevalence_pct: {
    label: 'Obesity Prevalence',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },

  // ── Environment (extended) ──────────────────────────────────────────
  arable_land_pct: {
    label: 'Arable Land',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  agricultural_land_pct: {
    label: 'Agricultural Land',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  pm25_air_pollution: {
    label: 'PM2.5 Air Pollution',
    unit: 'µg/m³',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)} µg/m³`,
  },
  protected_areas_pct: {
    label: 'Protected Areas',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  marine_protected_pct: {
    label: 'Marine Protected Areas',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },

  // ── Energy & Infrastructure (extended) ──────────────────────────────
  energy_use_per_capita: {
    label: 'Energy Use per Capita',
    unit: 'kg oil equivalent',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${Math.round(v).toLocaleString()} kg`,
  },
  electricity_consumption_kwh: {
    label: 'Electricity Consumption',
    unit: 'kWh per capita',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${Math.round(v).toLocaleString()} kWh`,
  },
  air_transport_departures: {
    label: 'Air Transport Departures',
    unit: 'departures',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: fmtPop,
  },

  // ── Gender Equity ──────────────────────────────────────────────────
  women_in_parliament_pct: {
    label: 'Women in Parliament',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  female_labor_force_pct: {
    label: 'Female Labor Force',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  gender_parity_primary: {
    label: 'Gender Parity (Primary)',
    unit: 'ratio',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => v.toFixed(2),
  },
  gender_parity_secondary: {
    label: 'Gender Parity (Secondary)',
    unit: 'ratio',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => v.toFixed(2),
  },

  // ── Composite ──────────────────────────────────────────────────────
  income_group_rank: {
    label: 'Income Group',
    unit: '1=High to 5=Low',
    transform: 'linear',
    breakCount: 5,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => {
      const labels = { 1: 'High (OECD)', 2: 'High', 3: 'Upper Middle', 4: 'Lower Middle', 5: 'Low' }
      return labels[Math.round(v)] || String(Math.round(v))
    },
  },

  // ── WHO Global Health Observatory ──────────────────────────────────
  life_expectancy_who: {
    label: 'Life Expectancy',
    unit: 'years',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)} yr`,
  },
  under5_mortality: {
    label: 'Under-5 Mortality',
    unit: 'per 1,000',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}/1k`,
  },
  maternal_mortality: {
    label: 'Maternal Mortality',
    unit: 'per 100,000',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${Math.round(v)}/100k`,
  },
  ncd_mortality_pct: {
    label: 'NCD Mortality 30-70',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  malaria_incidence: {
    label: 'Malaria Incidence',
    unit: 'per 1,000',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}/1k`,
  },
  hiv_prevalence: {
    label: 'HIV Prevalence',
    unit: '%',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  tb_incidence: {
    label: 'TB Incidence',
    unit: 'per 100,000',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${Math.round(v)}/100k`,
  },
  dtp3_coverage: {
    label: 'DTP3 Coverage',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${Math.round(v)}%`,
  },
  measles_coverage: {
    label: 'Measles Vaccine Coverage',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${Math.round(v)}%`,
  },
  physicians_per_10k: {
    label: 'Physicians',
    unit: 'per 10,000',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}/10k`,
  },
  nurses_per_10k: {
    label: 'Nurses & Midwives',
    unit: 'per 10,000',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}/10k`,
  },
  skilled_birth_pct: {
    label: 'Skilled Birth Attendance',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  obesity_pct: {
    label: 'Obesity (BMI≥30)',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  alcohol_per_capita: {
    label: 'Alcohol per Capita',
    unit: 'litres',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)} L`,
  },
  tobacco_smoking_pct: {
    label: 'Tobacco Smoking',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  safe_sanitation_pct: {
    label: 'Safe Sanitation',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${Math.round(v)}%`,
  },
  safe_water_pct: {
    label: 'Safe Drinking Water',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${Math.round(v)}%`,
  },
  child_wasting_pct: {
    label: 'Child Wasting (<5)',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  child_stunting_pct: {
    label: 'Child Stunting (<5)',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  health_spending_usd: {
    label: 'Health Spending/Capita',
    unit: 'USD',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => fmtMoney(v),
  },

  // ── FAO Aquastat — Water Resources ─────────────────────────────
  water_stress_pct: {
    label: 'Water Stress',
    unit: '%',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  freshwater_per_capita: {
    label: 'Freshwater/Capita',
    unit: 'm³',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k m³` : `${Math.round(v)} m³`),
  },
  freshwater_total_km3: {
    label: 'Total Freshwater',
    unit: 'bn m³',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)} bn m³`,
  },
  water_withdrawal_km3: {
    label: 'Water Withdrawal',
    unit: 'bn m³',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)} bn m³`,
  },
  ag_water_pct: {
    label: 'Agricultural Water Use',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  industrial_water_pct: {
    label: 'Industrial Water Use',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  domestic_water_pct: {
    label: 'Domestic Water Use',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  irrigated_land_pct: {
    label: 'Irrigated Land',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  safe_drinking_water_pct: {
    label: 'Safe Drinking Water',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${Math.round(v)}%`,
  },
  basic_water_pct: {
    label: 'Basic Water Access',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${Math.round(v)}%`,
  },
  safe_sanitation_wb_pct: {
    label: 'Safe Sanitation',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${Math.round(v)}%`,
  },
  sdg_water_stress_pct: {
    label: 'SDG 6.4.2 Water Stress',
    unit: '%',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },

  // ── WTO Trade Statistics ────────────────────────────────────
  exports_usd: {
    label: 'Merchandise Exports',
    unit: 'US$',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) =>
      v >= 1e12
        ? `$${(v / 1e12).toFixed(1)}T`
        : v >= 1e9
          ? `$${(v / 1e9).toFixed(1)}B`
          : v >= 1e6
            ? `$${(v / 1e6).toFixed(0)}M`
            : `$${v.toFixed(0)}`,
  },
  imports_usd: {
    label: 'Merchandise Imports',
    unit: 'US$',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) =>
      v >= 1e12
        ? `$${(v / 1e12).toFixed(1)}T`
        : v >= 1e9
          ? `$${(v / 1e9).toFixed(1)}B`
          : v >= 1e6
            ? `$${(v / 1e6).toFixed(0)}M`
            : `$${v.toFixed(0)}`,
  },
  trade_balance_usd: {
    label: 'Trade Balance',
    unit: 'US$',
    transform: 'symlog',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'diverging',
    nullThreshold: 0,
    formatValue: (v) => {
      const abs = Math.abs(v)
      const sign = v < 0 ? '-' : ''
      return abs >= 1e12
        ? `${sign}$${(abs / 1e12).toFixed(1)}T`
        : abs >= 1e9
          ? `${sign}$${(abs / 1e9).toFixed(1)}B`
          : abs >= 1e6
            ? `${sign}$${(abs / 1e6).toFixed(0)}M`
            : `${sign}$${abs.toFixed(0)}`
    },
  },
  trade_pct_gdp: {
    label: 'Trade Openness',
    unit: '% of GDP',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  exports_pct_gdp: {
    label: 'Exports (% of GDP)',
    unit: '%',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  imports_pct_gdp: {
    label: 'Imports (% of GDP)',
    unit: '%',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlGnBu',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  hightech_exports_pct: {
    label: 'High-Tech Exports',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
  current_account_usd: {
    label: 'Current Account Balance',
    unit: 'US$',
    transform: 'symlog',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'diverging',
    nullThreshold: 0,
    formatValue: (v) => {
      const abs = Math.abs(v)
      const sign = v < 0 ? '-' : ''
      return abs >= 1e12
        ? `${sign}$${(abs / 1e12).toFixed(1)}T`
        : abs >= 1e9
          ? `${sign}$${(abs / 1e9).toFixed(1)}B`
          : abs >= 1e6
            ? `${sign}$${(abs / 1e6).toFixed(0)}M`
            : `${sign}$${abs.toFixed(0)}`
    },
  },
  service_exports_usd: {
    label: 'Service Exports',
    unit: 'US$',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'RdYlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) =>
      v >= 1e12
        ? `$${(v / 1e12).toFixed(1)}T`
        : v >= 1e9
          ? `$${(v / 1e9).toFixed(1)}B`
          : v >= 1e6
            ? `$${(v / 1e6).toFixed(0)}M`
            : `$${v.toFixed(0)}`,
  },

  // ── Joshua Project — People Group metrics ──
  people_group_count: {
    label: 'People Groups',
    unit: 'groups',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'YlOrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => Math.round(v).toLocaleString(),
  },
  unreached_people_groups: {
    label: 'Unreached People Groups',
    unit: 'groups',
    transform: 'log',
    breakCount: 7,
    colorScheme: 'OrRd',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => Math.round(v).toLocaleString(),
  },
  pct_evangelical: {
    label: 'Evangelical %',
    unit: '%',
    transform: 'linear',
    breakCount: 7,
    colorScheme: 'YlGn',
    scaleType: 'relative',
    nullThreshold: 0,
    formatValue: (v) => `${v.toFixed(1)}%`,
  },
}

// ── Color Schemes ────────────────────────────────────────────────────────
// 7-step palettes, each RGBA. Perceptually distinct, high contrast against
// all terrain types (desert, ocean, forest, ice).

export const COLOR_SCHEMES = {
  // Sequential — light to dark
  YlOrRd: [
    [255, 255, 204, 200],
    [254, 217, 142, 200],
    [254, 153, 41, 200],
    [253, 141, 60, 200],
    [240, 59, 32, 200],
    [189, 0, 38, 200],
    [128, 0, 38, 220],
  ],
  YlGnBu: [
    [255, 255, 217, 200],
    [199, 233, 180, 200],
    [127, 205, 187, 200],
    [65, 182, 196, 200],
    [29, 145, 192, 200],
    [34, 94, 168, 200],
    [12, 44, 132, 220],
  ],
  RdPu: [
    [255, 247, 243, 200],
    [253, 224, 221, 200],
    [252, 197, 192, 200],
    [250, 159, 181, 200],
    [247, 104, 161, 200],
    [197, 27, 138, 200],
    [122, 1, 119, 220],
  ],
  YlGn: [
    [255, 255, 229, 200],
    [217, 240, 163, 200],
    [173, 221, 142, 200],
    [120, 198, 121, 200],
    [65, 171, 93, 200],
    [35, 132, 67, 200],
    [0, 90, 50, 220],
  ],
  OrRd: [
    [254, 240, 217, 200],
    [253, 212, 158, 200],
    [253, 187, 132, 200],
    [252, 141, 89, 200],
    [239, 101, 72, 200],
    [215, 48, 31, 200],
    [153, 0, 0, 220],
  ],
  // Diverging — meaningful zero or midpoint
  RdYlGn: [
    [215, 48, 39, 220],
    [244, 109, 67, 200],
    [253, 174, 97, 200],
    [255, 255, 191, 180],
    [166, 217, 106, 200],
    [102, 189, 99, 200],
    [26, 152, 80, 220],
  ],
  RdBu: [
    [178, 24, 43, 220],
    [214, 96, 77, 200],
    [244, 165, 130, 200],
    [247, 247, 247, 180],
    [146, 197, 222, 200],
    [67, 147, 195, 200],
    [33, 102, 172, 220],
  ],
  // Reversed RdBu — low values blue, high values red. Used for Getis-Ord Gi*
  // hot-spot maps where a HIGH z-score should read as a red "hot" cluster.
  RdBu_r: [
    [33, 102, 172, 220],
    [67, 147, 195, 200],
    [146, 197, 222, 200],
    [247, 247, 247, 180],
    [244, 165, 130, 200],
    [214, 96, 77, 200],
    [178, 24, 43, 220],
  ],
}

// Null / no-data color — neutral dark grey
export const NULL_COLOR = [120, 120, 120, 150]

// ── Symlog transform ─────────────────────────────────────────────────────
// Symmetric logarithmic: linear near zero, log for larger magnitudes.
// Source: Gemini report pp. 4–6.

const DEFAULT_LINTHRESH = 0.1

function symlog(x, C = DEFAULT_LINTHRESH) {
  const abs = Math.abs(x)
  if (abs <= C) return x
  return Math.sign(x) * C * (1 + Math.log10(abs / C))
}

function symlogInverse(y, C = DEFAULT_LINTHRESH) {
  const abs = Math.abs(y)
  if (abs <= C) return y
  return Math.sign(y) * C * Math.pow(10, abs / C - 1)
}

// ── Significant-digit rounding (Phase 3) ─────────────────────────────────
// significantRound(1234567, 3) → 1230000
// significantRound(0.004567, 3) → 0.00457
// significantRound(-250.3, 3)  → -250

function significantRound(v, sigFigs) {
  if (v === 0) return 0
  if (!Number.isFinite(v)) return v
  const abs = Math.abs(v)
  // Guard against overflow: if magnitude is too large for pow(10), return as-is
  const magnitude = Math.floor(Math.log10(abs))
  if (magnitude - sigFigs + 1 > 300 || magnitude - sigFigs + 1 < -300) return v
  const factor = Math.pow(10, magnitude - sigFigs + 1)
  if (!Number.isFinite(factor) || factor === 0) return v
  return Math.round(v / factor) * factor
}

// ── Pure-JS quantile helper ──────────────────────────────────────────────

function jsQuantile(sortedValues, p) {
  if (sortedValues.length === 0) return 0
  if (sortedValues.length === 1) return sortedValues[0]
  const idx = p * (sortedValues.length - 1)
  const lo = Math.floor(idx)
  const hi = Math.ceil(idx)
  if (lo === hi) return sortedValues[lo]
  return sortedValues[lo] + (sortedValues[hi] - sortedValues[lo]) * (idx - lo)
}

// ── Classification Engine ────────────────────────────────────────────────

export class ClassificationEngine {
  constructor() {
    this.breakCache = new Map()
    // Per-op hint from the Spatial Analysis Advisor. Populated by
    // query-router before executing a pipeline op and cleared afterwards.
    // computeBreaks consults this when choosing a strategy.
    this._advisorHint = null
  }

  /**
   * Store a Spatial Analysis Advisor hint for the next classification call.
   * The hint is consulted only when it is compatible with the data profile
   * — otherwise the auto-detected strategy wins.
   */
  setAdvisorHint(hint) {
    this._advisorHint = hint || null
  }

  /**
   * Map a Spatial Advisor classification id to a profiler selectedPath.
   * Returns null when the hint can't be mapped onto one of the engine's
   * existing transform paths (in which case the profiler decides).
   */
  _advisorHintToPath(suggested) {
    switch (suggested) {
      case 'log-quantile':
        return 'logQuantile'
      case 'quantile':
      case 'jenks':
      case 'natural-breaks':
      case 'equal-interval':
        return 'linearQuantile'
      case 'standard-deviation':
      case 'diverging':
        return 'diverging'
      default:
        return null
    }
  }

  /**
   * A hint is compatible with a data profile when applying it won't produce
   * a degenerate classification. Reject log-quantile on data that contains
   * meaningful negatives, and reject diverging on data that never crosses
   * zero.
   */
  _isPathCompatible(advisorPath, dataProfile) {
    if (!dataProfile) return false
    if (dataProfile.selectedPath === 'discrete') return false // always defer
    if (advisorPath === 'logQuantile') {
      return !(dataProfile.min < 0)
    }
    if (advisorPath === 'diverging') {
      return dataProfile.min < 0 && dataProfile.max > 0
    }
    return true
  }

  /**
   * Resolve a metric key to its profile. Returns a default profile if unknown.
   */
  resolveProfile(metricKey) {
    const key = (metricKey || 'population').toLowerCase()
    return METRIC_PROFILES[key] || METRIC_PROFILES.population
  }

  /**
   * Compute quantile break thresholds from a values array.
   *
   * Phase 2 upgrade: uses the data profiler for dynamic routing based on
   * actual data distribution (skewness, kurtosis), not just the hardcoded
   * transform in METRIC_PROFILES. The profile.transform field is kept as
   * a hint for anchored breaks but the profiler has final authority.
   *
   * @param {number[]} values - raw data values for all features
   * @param {object} profile - MetricProfile for this metric
   * @param {string} scaleOverride - 'relative'|'absolute'|null (use profile default)
   * @param {number[]} globalValues - full global dataset values (for absolute scale)
   * @param {string} adm - 'ADM0'|'ADM1'|etc. (for anchor lookup)
   * @param {string} metricKey - key into METRIC_PROFILES (for anchor lookup)
   * @returns {number[]} threshold values in original (non-transformed) space
   */
  computeBreaks(values, profile, scaleOverride, globalValues, adm, metricKey) {
    const scaleMode = scaleOverride || profile.scaleType || 'relative'
    const breakCount = profile.breakCount || 7
    const numThresholds = breakCount - 1 // 6 thresholds for 7 buckets

    // Choose which values to compute quantiles over
    let sourceValues
    if (scaleMode === 'absolute' && globalValues?.length > 0) {
      sourceValues = globalValues
    } else if (scaleMode === 'absolute' && adm && metricKey) {
      // Try to use anchored min/max to generate synthetic breaks
      const anchorKey = metricKey.toLowerCase()
      const anchor = METRIC_ANCHORS[anchorKey]?.[adm]
      if (anchor) {
        return this._anchoredBreaks(anchor.min, anchor.max, profile, numThresholds)
      }
      sourceValues = values
    } else {
      sourceValues = values
    }

    // ── Phase 2: Use data profiler for routing ──
    // Critical: null means "no filter" — preserve it. Only default to 0 when undefined.
    const nullThresh = profile.nullThreshold !== undefined ? profile.nullThreshold : 0
    const dataProfile = profileData(sourceValues, {
      nullThreshold: nullThresh,
      requestedClasses: breakCount,
      metricId: metricKey || 'unknown',
    })

    const filtered = dataProfile.validValues

    // Advisor hint: if the Spatial Analysis Advisor recommended a specific
    // strategy AND it's compatible with the profiler's detected path, use
    // it. Otherwise let the profiler's auto-detection win and log the
    // override so we can tune the advisor.
    const hint = this._advisorHint
    if (hint && hint.suggestedClassification && hint.suggestedClassification !== 'auto') {
      const advisorPath = this._advisorHintToPath(hint.suggestedClassification)
      if (advisorPath && this._isPathCompatible(advisorPath, dataProfile)) {
        if (advisorPath !== dataProfile.selectedPath) {
          console.debug(
            `[spatial-advisor] using advisor strategy ${hint.suggestedClassification} (override ${dataProfile.selectedPath})`
          )
          dataProfile.selectedPath = advisorPath
        }
      } else if (advisorPath) {
        console.debug(
          `[spatial-advisor] overriding advisor (${hint.suggestedClassification}) — incompatible with profile, keeping ${dataProfile.selectedPath}`
        )
      }
    }

    // Route by profiler decision — each calculator returns raw thresholds
    let rawThresholds
    if (dataProfile.selectedPath === 'discrete') {
      rawThresholds = this._discreteBreaks(filtered, numThresholds)
    } else if (dataProfile.selectedPath === 'diverging') {
      rawThresholds = this._divergingBreaks(filtered, numThresholds)
    } else if (dataProfile.selectedPath === 'logQuantile') {
      rawThresholds = this._logQuantileBreaks(filtered, numThresholds)
    } else {
      rawThresholds = this._linearQuantileBreaks(filtered, numThresholds)
    }

    // Phase 3: class-aware rounding — pass filtered values so rounding
    // can verify no data point jumps brackets
    return this._cleanThresholds(rawThresholds, filtered)
  }

  /**
   * Log-quantile breaks: log10 → quantiles → back to original space.
   *
   * Phase 2 fix: zeros get a dedicated lowest class instead of being
   * silently dropped. The zero class uses a [0, 0] boundary prepended
   * to the log-quantile thresholds computed from positives.
   */
  _logQuantileBreaks(values, numThresholds) {
    const positives = values.filter((v) => v > 0)
    const hasZeros = values.some((v) => v === 0)

    if (positives.length === 0) {
      return this._discreteBreaks(values, numThresholds)
    }

    // If zeros present, dedicate first threshold to zero boundary
    // and compute remaining thresholds from positives
    let effectiveThresholds = numThresholds
    if (hasZeros && effectiveThresholds > 1) {
      effectiveThresholds = numThresholds - 1
    }

    const logValues = positives.map((v) => Math.log10(v)).sort((a, b) => a - b)

    const thresholds = []

    // Prepend zero boundary if we have zeros
    if (hasZeros) {
      thresholds.push(0)
    }

    for (let i = 1; i <= effectiveThresholds; i++) {
      const p = i / (effectiveThresholds + 1)
      const logThresh = jsQuantile(logValues, p)
      thresholds.push(Math.pow(10, logThresh))
    }

    // Ensure we have exactly numThresholds
    while (thresholds.length > numThresholds) thresholds.pop()
    while (thresholds.length < numThresholds) {
      // Extend with the max value
      thresholds.push(positives[positives.length - 1] || 1)
    }

    return thresholds
  }

  /**
   * Linear quantile breaks.
   *
   * Phase 2 fix: duplicate quantiles are merged (reducing class count)
   * instead of being nudged with multiplicative jitter.
   */
  _linearQuantileBreaks(values, numThresholds) {
    const sorted = [...values].sort((a, b) => a - b)
    const thresholds = []
    for (let i = 1; i <= numThresholds; i++) {
      const p = i / (numThresholds + 1)
      thresholds.push(jsQuantile(sorted, p))
    }
    return thresholds
  }

  /**
   * Diverging breaks with Symlog transform.
   *
   * Phase 2 rewrite: replaces raw quantile approach with Symmetric
   * Logarithmic (Symlog) transform. Allocates classes proportionally
   * between negative and positive sides based on element count.
   *
   * Pipeline:
   *   1. Anchor zero (non-negotiable)
   *   2. Split into negative/positive subarrays
   *   3. Allocate classes proportionally
   *   4. Symlog-transform each side, compute quantile boundaries
   *   5. Inverse-Symlog to recover raw thresholds
   *   6. Rejoin: [negatives..., 0, positives...]
   *
   * Source: Gemini report pp. 4–6.
   */
  _divergingBreaks(values, numThresholds) {
    const negatives = values.filter((v) => v < 0).sort((a, b) => a - b)
    const positives = values.filter((v) => v > 0).sort((a, b) => a - b)

    // Determine linthresh C from data: use the smallest positive magnitude,
    // or default to 0.1 for percentage-type data
    const allAbs = values.filter((v) => v !== 0).map((v) => Math.abs(v))
    const C =
      allAbs.length > 0 ? Math.min(DEFAULT_LINTHRESH, Math.min(...allAbs) * 0.5) : DEFAULT_LINTHRESH

    // Allocate classes proportionally (minus 1 for zero anchor)
    const availableSlots = numThresholds - 1 // reserve 1 for zero
    let negSlots, posSlots

    if (negatives.length === 0) {
      negSlots = 0
      posSlots = availableSlots
    } else if (positives.length === 0) {
      negSlots = availableSlots
      posSlots = 0
    } else {
      const total = negatives.length + positives.length
      negSlots = Math.max(1, Math.round(availableSlots * (negatives.length / total)))
      posSlots = availableSlots - negSlots
      // Ensure at least 1 slot per side if data exists
      if (posSlots === 0 && positives.length > 0) {
        posSlots = 1
        negSlots = availableSlots - 1
      }
    }

    const thresholds = []

    // Negative thresholds via Symlog
    if (negSlots > 0 && negatives.length > 0) {
      if (negatives.length === 1) {
        thresholds.push(negatives[0])
      } else {
        const symNeg = negatives.map((v) => symlog(v, C)).sort((a, b) => a - b)
        for (let i = 1; i <= negSlots; i++) {
          const p = i / (negSlots + 1)
          const symThresh = jsQuantile(symNeg, p)
          thresholds.push(symlogInverse(symThresh, C))
        }
      }
    }

    // Zero anchor
    thresholds.push(0)

    // Positive thresholds via Symlog
    if (posSlots > 0 && positives.length > 0) {
      if (positives.length === 1) {
        thresholds.push(positives[0])
      } else {
        const symPos = positives.map((v) => symlog(v, C)).sort((a, b) => a - b)
        for (let i = 1; i <= posSlots; i++) {
          const p = i / (posSlots + 1)
          const symThresh = jsQuantile(symPos, p)
          thresholds.push(symlogInverse(symThresh, C))
        }
      }
    }

    // Pad if needed (shouldn't happen normally)
    while (thresholds.length < numThresholds) {
      thresholds.push(thresholds[thresholds.length - 1] + 0.001)
    }

    // Trim to exact count
    const result = thresholds.slice(0, numThresholds)

    // Ensure strictly ascending — use absolute epsilon, not multiplicative jitter
    for (let i = 1; i < result.length; i++) {
      if (result[i] <= result[i - 1]) {
        const eps = Math.max(Math.abs(result[i - 1]) * Number.EPSILON * 100, 1e-15)
        const next = result[i - 1] + eps
        result[i] = Number.isFinite(next) && next > result[i - 1] ? next : result[i - 1] + 1
      }
    }

    return result
  }

  /**
   * Discrete breaks for sparse data (n < k) or degenerate inputs.
   *
   * Phase 2 addition: replaces equal-interval fallback per Locked Decision #5.
   * Each unique value gets its own class. If there are more unique values
   * than numThresholds, falls back to linear quantiles of the uniques.
   */
  _discreteBreaks(values, numThresholds) {
    if (values.length === 0) {
      // Degenerate: return safe defaults
      return Array.from({ length: numThresholds }, (_, i) => (i + 1) * 10)
    }

    const sorted = [...values].sort((a, b) => a - b)
    const uniques = [...new Set(sorted)].sort((a, b) => a - b)

    if (uniques.length <= 1) {
      // All identical or single value — create a narrow range around it
      const v = uniques[0] || 0
      const rawRange = Math.max(Math.abs(v) * 0.1, 1)
      const range = Number.isFinite(rawRange) ? rawRange : 1
      const base = v - range
      const step = (2 * range) / (numThresholds + 1)

      // Guard: if any arithmetic overflows, return simple linear steps
      if (!Number.isFinite(base) || !Number.isFinite(step)) {
        return Array.from({ length: numThresholds }, (_, i) => i + 1)
      }

      const breaks = []
      for (let i = 0; i < numThresholds; i++) {
        const candidate = base + (i + 1) * step
        breaks.push(
          Number.isFinite(candidate)
            ? candidate
            : breaks.length > 0
              ? breaks[breaks.length - 1] + 1
              : v
        )
      }
      return breaks
    }

    if (uniques.length <= numThresholds) {
      // Fewer unique values than thresholds — use midpoints between uniques
      const breaks = []
      for (let i = 0; i < uniques.length - 1; i++) {
        breaks.push((uniques[i] + uniques[i + 1]) / 2)
      }
      // Pad remaining with values above max, guarding overflow
      while (breaks.length < numThresholds) {
        const last = breaks.length > 0 ? breaks[breaks.length - 1] : uniques[uniques.length - 1]
        const increment = Math.max(Math.abs(last) * 0.1, 1)
        const next = last + increment
        breaks.push(Number.isFinite(next) ? next : last + 1)
      }
      return breaks.slice(0, numThresholds)
    }

    // More unique values than thresholds — use linear quantiles on uniques
    return this._linearQuantileBreaks(uniques, numThresholds)
  }

  /**
   * Generate breaks from anchored min/max (absolute scale without full data).
   *
   * Phase 4 upgrade: uses profiler routing to determine which calculator to
   * use, rather than relying solely on profile.transform. Generates a synthetic
   * dataset spanning the global range and routes through the profiler.
   *
   * @param {number} min - global minimum from metric-anchors.js
   * @param {number} max - global maximum from metric-anchors.js
   * @param {object} profile - metric profile
   * @param {number} numThresholds - number of threshold values (breakCount - 1)
   * @returns {number[]} threshold values
   */
  _anchoredBreaks(min, max, profile, numThresholds) {
    // For diverging data (crosses zero), use Symlog-based equal spacing
    if (min < 0 && max > 0) {
      return this._divergingAnchoredBreaks(min, max, numThresholds)
    }

    // For positive data, use log-spaced breaks if the range spans > 3 orders of magnitude
    if (min > 0 && max > 0 && Math.log10(max / min) > 3) {
      const logMin = Math.log10(min)
      const logMax = Math.log10(max)
      const thresholds = []
      for (let i = 1; i <= numThresholds; i++) {
        const p = i / (numThresholds + 1)
        const logVal = logMin + p * (logMax - logMin)
        thresholds.push(Math.pow(10, logVal))
      }
      return this._cleanThresholds(thresholds)
    }

    // Default: equal-interval
    return this._equalIntervalBreaks(min, max, numThresholds)
  }

  /**
   * Diverging anchored breaks: Symlog-spaced with zero anchor.
   * Used for absolute-scale diverging metrics (e.g., global growth rate comparison).
   */
  _divergingAnchoredBreaks(min, max, numThresholds) {
    const C = DEFAULT_LINTHRESH
    const availableSlots = numThresholds - 1 // reserve 1 for zero

    // Allocate proportionally based on magnitude
    const absMin = Math.abs(min)
    const absMax = Math.abs(max)
    const total = absMin + absMax
    let negSlots = Math.max(1, Math.round(availableSlots * (absMin / total)))
    let posSlots = availableSlots - negSlots
    if (posSlots === 0 && max > 0) {
      posSlots = 1
      negSlots = availableSlots - 1
    }

    const thresholds = []

    // Negative side: equal Symlog spacing
    for (let i = 1; i <= negSlots; i++) {
      const p = i / (negSlots + 1)
      const symVal = symlog(min, C) + p * (symlog(0, C) - symlog(min, C))
      thresholds.push(symlogInverse(symVal, C))
    }

    thresholds.push(0)

    // Positive side: equal Symlog spacing
    for (let i = 1; i <= posSlots; i++) {
      const p = i / (posSlots + 1)
      const symVal = symlog(0, C) + p * (symlog(max, C) - symlog(0, C))
      thresholds.push(symlogInverse(symVal, C))
    }

    // Pad if needed
    while (thresholds.length < numThresholds) {
      thresholds.push(thresholds[thresholds.length - 1] + 0.001)
    }

    return this._cleanThresholds(thresholds.slice(0, numThresholds))
  }

  /**
   * Equal-interval fallback (used only for anchored breaks now).
   */
  _equalIntervalBreaks(min, max, numThresholds) {
    const range = max - min
    if (range <= 0) return Array.from({ length: numThresholds }, (_, i) => min + i + 1)
    const step = range / (numThresholds + 1)
    return Array.from({ length: numThresholds }, (_, i) => min + (i + 1) * step)
  }

  /**
   * Class-aware rounding: round thresholds to clean numbers while ensuring
   * no data point changes brackets.
   *
   * Phase 3 upgrade: tries progressively finer rounding (3-sig → 4-sig →
   * 5-sig → no-round). For each threshold, checks that no actual data point
   * exists between the exact and proposed rounded value. If rounding would
   * cause bracket jumping, falls back to finer precision.
   *
   * @param {number[]} thresholds - raw thresholds from break calculators
   * @param {number[]} [validValues] - the filtered data values (for bracket safety check)
   * @returns {number[]} cleaned thresholds, strictly ascending
   */
  _cleanThresholds(thresholds, validValues) {
    // If no data values provided, fall back to cosmetic-only rounding
    const sorted = validValues?.length ? [...validValues].sort((a, b) => a - b) : null

    const cleaned = thresholds.map((T) => {
      if (T === 0) return 0

      // Try progressively finer rounding: 3-sig → 4-sig → 5-sig → exact
      for (const sigFigs of [3, 4, 5, null]) {
        const candidate = sigFigs === null ? T : significantRound(T, sigFigs)

        // If no data to check against, accept the first round
        if (!sorted) return candidate

        // Class-aware check: would any data point jump brackets?
        // A point jumps if it was above T but is now at-or-below candidate,
        // or was at-or-below T but is now above candidate.
        const safe = !sorted.some(
          (x) =>
            (x > T && x <= candidate) || // was above, now at-or-below
            (x <= T && x > candidate) // was at-or-below, now above
        )

        if (safe) return candidate
      }
      // Shouldn't reach here since null (exact) is always safe
      return T
    })

    // Ensure strictly ascending — find the smallest increment that actually changes the float
    for (let i = 1; i < cleaned.length; i++) {
      if (cleaned[i] <= cleaned[i - 1]) {
        // Try progressively larger epsilons until we get a strictly larger finite result
        const prev = cleaned[i - 1]
        let found = false
        for (const scale of [1e-10, 1e-8, 1e-6, 1e-4, 1e-2, 1]) {
          const eps = Math.max(Math.abs(prev) * scale, scale)
          const candidate = prev + eps
          if (Number.isFinite(candidate) && candidate > prev) {
            cleaned[i] = candidate
            found = true
            break
          }
        }
        if (!found) cleaned[i] = prev // Can't nudge — extremely rare edge case
      }
    }

    // Final safety: replace any non-finite values
    for (let i = 0; i < cleaned.length; i++) {
      if (!Number.isFinite(cleaned[i])) {
        cleaned[i] = i === 0 ? 0 : cleaned[i - 1]
      }
    }

    return cleaned
  }

  /**
   * Assign a bracket index (0–7) to a single value.
   * 0 = null/no-data (grey). 1–7 = data brackets.
   */
  classify(value, thresholds, profile) {
    if (value == null || isNaN(value)) return 0
    const nullThresh = profile.nullThreshold
    if (nullThresh !== null && value <= nullThresh) return 0

    for (let i = 0; i < thresholds.length; i++) {
      if (value <= thresholds[i]) return i + 1
    }
    return thresholds.length + 1 // highest bracket
  }

  /**
   * Get the RGBA color for a bracket index.
   */
  getColor(bracketIndex, profile) {
    if (bracketIndex === 0) return NULL_COLOR
    const scheme = COLOR_SCHEMES[profile.colorScheme] || COLOR_SCHEMES.YlOrRd
    const idx = Math.min(bracketIndex - 1, scheme.length - 1)
    return scheme[idx]
  }

  /**
   * Main entry point: classify all features and return colors + legend data.
   *
   * @param {Array} features - array of { id, value } (or GeoJSON features)
   * @param {string} metricKey - key into METRIC_PROFILES
   * @param {string} adm - 'ADM0'|'ADM1'|etc.
   * @param {string} scaleMode - 'relative'|'absolute'
   * @param {number[]} globalValues - global dataset values (for absolute scale)
   * @returns {{ colorMap: Map, thresholds: number[], profile: object, brackets: Map }}
   */
  classifyFeatures(features, metricKey, adm, scaleMode, globalValues) {
    const profile = this.resolveProfile(metricKey)
    const values = features.map((f) => f.value ?? f).filter((v) => v != null && !isNaN(v))

    const thresholds = this.computeBreaks(values, profile, scaleMode, globalValues, adm, metricKey)

    const colorMap = new Map()
    const brackets = new Map()

    for (const f of features) {
      const id = f.id ?? f
      const value = f.value ?? f
      const bracket = this.classify(value, thresholds, profile)
      const color = this.getColor(bracket, profile)
      colorMap.set(id, color)
      brackets.set(id, bracket)
    }

    return { colorMap, thresholds, profile, brackets }
  }

  /**
   * Classify features using pre-computed breaks (from catalog or DuckDB).
   * Skips the expensive computeBreaks() step entirely.
   *
   * @param {Array} features - array of { id, value } (or GeoJSON features)
   * @param {number[]} precomputedBreaks - 6 threshold values (pre-computed)
   * @param {string} metricKey - key into METRIC_PROFILES
   * @returns {{ colorMap: Map, thresholds: number[], profile: object, brackets: Map }}
   */
  classifyWithBreaks(features, precomputedBreaks, metricKey) {
    const profile = this.resolveProfile(metricKey)
    const thresholds = precomputedBreaks

    const colorMap = new Map()
    const brackets = new Map()

    for (const f of features) {
      const id = f.id ?? f
      const value = f.value ?? f
      const bracket = this.classify(value, thresholds, profile)
      const color = this.getColor(bracket, profile)
      colorMap.set(id, color)
      brackets.set(id, bracket)
    }

    return { colorMap, thresholds, profile, brackets }
  }

  /**
   * Build legend entries for the current classification.
   *
   * Phase 3 upgrade: integer vs continuous legend formatting.
   * - Integer data: "100 to 200" / "201 to 300" (inclusive upper bound, +1 gap)
   * - Continuous data: ">100 to 200" / ">200 to 300" (exclusive lower bound prefix)
   *
   * The isInteger flag can be passed explicitly or detected from thresholds.
   *
   * @param {number[]} thresholds - 6 threshold values
   * @param {object} profile - metric profile
   * @param {string} scaleMode - 'relative'|'absolute'
   * @param {string} adm - 'ADM0'|'ADM1'|etc.
   * @param {boolean} [isInteger] - force integer/continuous mode. If omitted, auto-detect.
   */
  buildLegend(thresholds, profile, scaleMode, adm, isInteger) {
    const scheme = COLOR_SCHEMES[profile.colorScheme] || COLOR_SCHEMES.YlOrRd
    const fmt = profile.formatValue || ((v) => String(Math.round(v)))
    const numBrackets = Math.min(thresholds.length + 1, scheme.length)

    // Auto-detect integer mode if not explicitly provided
    const intMode =
      isInteger !== undefined ? isInteger : thresholds.every((t) => Number.isInteger(t))

    const entries = []
    for (let i = 0; i < numBrackets; i++) {
      const isFirst = i === 0
      const isLast = i >= thresholds.length
      const lower = isFirst ? null : thresholds[i - 1]
      const upper = isLast ? null : thresholds[i]

      let label
      if (isFirst && isLast) {
        // Single bracket — shouldn't happen normally
        label = 'All values'
      } else if (isFirst) {
        // First bracket: [min, T₁]
        label = `${fmt(upper)} or less`
      } else if (isLast) {
        // Last bracket: (Tₖ₋₁, +∞)
        if (intMode) {
          label = `${fmt(lower + 1)}+`
        } else {
          label = `>${fmt(lower)}`
        }
      } else {
        // Middle bracket: (Tᵢ₋₁, Tᵢ]
        if (intMode) {
          label = `${fmt(lower + 1)} to ${fmt(upper)}`
        } else {
          label = `>${fmt(lower)} to ${fmt(upper)}`
        }
      }

      entries.push({
        color: scheme[i],
        label,
        min: lower,
        max: upper,
        bracket: i + 1,
      })
    }

    // Add no-data entry
    entries.push({
      color: NULL_COLOR,
      label: 'No data',
      min: null,
      max: null,
      bracket: 0,
    })

    const scaleModeLabel =
      scaleMode === 'absolute' ? `Global scale` : `Local scale (${adm || 'ADM0'})`

    return {
      visible: true,
      title: profile.label || 'Legend',
      unit: profile.unit || '',
      type: 'classified',
      stops: entries,
      scaleMode: scaleModeLabel,
      collapsible: true,
    }
  }
}

// Singleton instance
export const classificationEngine = new ClassificationEngine()
